/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ActionContext, Module } from 'vuex';
import { getUserAutarkyState } from '@/api/homeV2Status';
import type { AutarkyStateType } from '@/api/homeV2Status/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { AUTARKY_UPGRADE_PROCESS_STATE } from '@/views_v2/home/const';
import RootStateType from '@/store/types';
import type { Store } from 'vuex';
import { StateType } from './types';
import generateMutations, { UPDATE_STATE_TYPE } from '../generate-mutations';

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    autarkyState: undefined,
    isAutarkyForbidCredit: undefined,
    isAutarkyUser: undefined,
    isAutarkyUpgradeUser: undefined,
  },
  actions: {
    /** 读取用户自营状态数据 */
    async getUserAutarkyState(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<AutarkyStateType> {
      /** 请求服务端新数据 */
      const autarkyState = await getUserAutarkyState();

      /** 更新缓存数据 */
      context.dispatch(UPDATE_STATE_TYPE, {
        autarkyState,
        isAutarkyForbidCredit: autarkyState.canApplyCredit === false,
        isAutarkyUser: autarkyState.autarky === true,
        isAutarkyUpgradeUser: autarkyState.autarky === true
          && autarkyState.autarkyUpgradeState
          && AUTARKY_UPGRADE_PROCESS_STATE.includes(autarkyState.autarkyUpgradeState),
      });

      return autarkyState;
    },

    /** 检查是否命中老户锁客 */
    async checkIsAutarkySucceed(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserAutarkyState');

        return !!context.state.isAutarkyForbidCredit;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },

    /** 检查是否老客升级用户 */
    async checkIsAutarkyUser(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserAutarkyState');

        return !!context.state.isAutarkyUser;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },
  },
};

export default generateMutations(moduleStore);

export const generateDispatchActions = (store: Store<RootStateType>): {
  getUserAutarkyState: () => Promise<AutarkyStateType>;
  checkIsAutarkySucceed: () => Promise<boolean>;
  checkIsAutarkyUser: () => Promise<boolean>;
} => ({
  getUserAutarkyState:
      (): Promise<AutarkyStateType> => store.dispatch('userAutarkyStatus/getUserAutarkyState') as unknown as Promise<AutarkyStateType>,
  checkIsAutarkySucceed:
      (): Promise<boolean> => store.dispatch('userAutarkyStatus/checkIsAutarkySucceed') as unknown as Promise<boolean>,
  checkIsAutarkyUser:
      (): Promise<boolean> => store.dispatch('userAutarkyStatus/checkIsAutarkyUser') as unknown as Promise<boolean>,
});
